import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';

import { fsErrorMessages } from '@repo/lib/messages/fsErrorMessages';

import { useGetDirectory } from '#directory/useGetDirectory';
import { useErrorMessage } from '#error-message/useErrorMessage';

import { useGetGroupSharedFiles } from './useGetGroupSharedFiles';

interface GetGroupDirectoryWithSharedProps {
  groupId?: number;
  directoryId?: string;
  useSharedEndpoint?: boolean;
  enabled?: boolean;
}

export function useGetGroupDirectoryWithShared(props: GetGroupDirectoryWithSharedProps) {
  const { groupId, directoryId, useSharedEndpoint, enabled = true } = props;

  const getShared = useGetGroupSharedFiles(groupId, enabled && !!useSharedEndpoint && groupId !== undefined);
  const getDirectory = useGetDirectory(directoryId, enabled && !useSharedEndpoint);

  const error: Error | null = useSharedEndpoint ? getShared.error : getDirectory.error;

  let dirs: DirectoryPublic[] = [];
  let files: FilePublicWithPermissions[] = [];
  if (useSharedEndpoint) {
    dirs = getShared.data?.dirs ?? [];
    files = getShared.data?.files ?? [];
  } else {
    dirs = getDirectory.data?.childDirs ?? [];
    files = getDirectory.data?.files ?? [];
  }

  const isLoading: boolean = useSharedEndpoint ? getShared.isLoading : getDirectory.isLoading;
  const isPending: boolean = useSharedEndpoint ? getShared.isPending : getDirectory.isPending;
  const isSuccess: boolean = useSharedEndpoint ? getShared.isSuccess : getDirectory.isSuccess;
  const isFetching: boolean = useSharedEndpoint ? getShared.isFetching : getDirectory.isFetching;
  const isError: boolean = useSharedEndpoint ? getShared.isError : getDirectory.isError;

  const errorMessage = useErrorMessage(error, fsErrorMessages);

  return {
    isLoading,
    isPending,
    isSuccess,
    isFetching,
    isError,
    directoryData: useSharedEndpoint ? undefined : getDirectory.data,
    fsItems: { files, dirs },
    error,
    errorMessage,
  };
}
